import { Button, ModalBody, ModalFooter, Text } from "@chakra-ui/react";
import { useApiError } from "@raiden/library/components/ReactHookForm/ApiError";
import FormStateRHF from "@raiden/library/components/ReactHookForm/FormStateRHF";
import { RENTAL_AREAS_BOOKINGS_CUSTOMER_TYPE_VALUE_PARTICULAR } from "@raiden/library/constants/rentalArea/booking";
import { usePreferences } from "@raiden/library/contexts/Preferences";
import useRequest from "@raiden/library/hooks/useRequest";
import generateApiUrl from "@raiden/library/libraries/utils/generateApiUrl";
import convertPublishDateToUtc from "@raiden/library/normalizers/rentalAreas/bookings/convertPublishDateCreateOrUpdate";
import dayjs from "dayjs";
import { useCallback } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { DetailHeaderRentalArea } from "../Details";
import { FormCreateBookings } from "../Form";

/**
 * @param {import("@raiden/library/types/Environment").Environment | null} environment
 * @param {import("@raiden/library/types/RentalArea").RentalArea} rentalArea
 * @param {string | undefined} date
 * @returns {import("@raiden/library/types/rentalArea/Booking").RentalAreaBookingsSpecificFormValues}
 */
function getDefaultValues(environment, rentalArea, date) {
  return {
    data: {
      environment_id: environment?.id ?? "",
      customer_id: null,
      area_id: rentalArea.id,
      customer_type: RENTAL_AREAS_BOOKINGS_CUSTOMER_TYPE_VALUE_PARTICULAR,
      company_name: "",
      first_name: "",
      last_name: "",
      address1: "",
      address2: "",
      zipcode: "",
      city: "",
      country_id: 319,
      email: "",
      phone_country: "FR",
      phone_number: "",
      start_at: date
        ? dayjs(date).format("YYYY-MM-DD")
        : dayjs().format("YYYY-MM-DD"),
      start_at_hour: rentalArea.opening_hour ?? "",
      end_at: "",
      end_at_hour: rentalArea.closing_hour ?? "",
      infos: "",
      capacity: 0,
    },
    meta: {
      auto_confirm: "0",
      notify_customer: true,
      customer_notification_message: "",
    },
  };
}

/**
 * @typedef {Object} Props
 * @property {import("@raiden/library/types/RentalArea").RentalArea} rentalArea
 * @property {string} [date]
 * @property {() => void} onClose
 * @property {() => void} onSuccess
 */
/**
 * @param {Props} props
 */
export function CreateHandlerBookings({
  rentalArea,
  date,
  onClose,
  onSuccess,
}) {
  const intl = useIntl();

  const { bestEnvironment } = usePreferences();

  /** @type {import("react-hook-form").UseFormReturn<import("@raiden/library/types/rentalArea/Booking").RentalAreaBookingsSpecificFormValues>}*/
  const form = useForm({
    defaultValues: getDefaultValues(bestEnvironment, rentalArea, date),
  });
  const { handleSubmit } = form;

  const { request, isLoading, toastSuccess } = useRequest();

  const { resolveApiError, resetApiErrors } = useApiError();

  const submit = useCallback(
    (event) => {
      event.preventDefault();
      handleSubmit((values) => {
        request(
          generateApiUrl({
            id: "@api.rental_areas.bookings.create",
          }),
          {
            method: "POST",
            body: {
              data: {
                ...values.data,
                start_at: convertPublishDateToUtc({
                  localDate: `${values.data.start_at} ${values.data.start_at_hour}`,
                }),
                end_at: convertPublishDateToUtc({
                  localDate: `${values.data.end_at} ${values.data.end_at_hour}`,
                }),
              },
              meta: {
                ...values.meta,
              },
            },
          },
        )
          .then((response) => {
            toastSuccess({
              title: intl.formatMessage({
                defaultMessage: "La création a bien été prise en compte.",
              }),
            });
            resetApiErrors();
            onSuccess?.();
          })
          .catch((response) => {
            resolveApiError({ response, form });
          });
      })();
    },
    [
      form,
      handleSubmit,
      intl,
      onSuccess,
      request,
      resetApiErrors,
      resolveApiError,
      toastSuccess,
    ],
  );

  return (
    <FormProvider {...form}>
      <ModalBody>
        <DetailHeaderRentalArea rentalArea={rentalArea} />

        <form
          id="createBookings"
          onSubmit={submit}
          autoComplete="off"
          noValidate>
          <FormCreateBookings rentalArea={rentalArea} />
        </form>
      </ModalBody>

      <ModalFooter mb="1rem">
        <FormStateRHF
          render={({ isDirty }) => (
            <Button
              form="createBookings"
              type="submit"
              colorScheme="brandPrimary"
              backgroundColor="brandPrimary.700"
              isLoading={isLoading}
              isDisabled={!isDirty}>
              <Text variant="button" color="inherit">
                <FormattedMessage defaultMessage="Envoyer ma demande" />
              </Text>
            </Button>
          )}
        />
      </ModalFooter>
    </FormProvider>
  );
}
